// Auth boundary. Real Entra sign-in via MSAL.js (PKCE, public client, no secret),
// behind the authConfigured() seam so the stub still applies if no client id is
// configured. Offline-first is preserved: MSAL caches accounts in localStorage,
// so initAuth() restores the signed-in identity from cache with NO network, and
// capture never blocks on auth. Only signIn() and token acquisition touch the
// network, and only when the user taps sign-in / a sync runs.
//
// The delegated access token's `roles` claim carries SSS.Agent for assigned
// users; the MCP validator gates on it (sss-mcp/docs/booth_tracker_headless_auth.md).
//
// v1.2 — THE CACHED ACCOUNT AND THE TOKEN ARE NOT THE SAME FACT.
// Through v1.1 initAuth() reported `authenticated: true` purely because
// getAllAccounts() returned an account out of localStorage; it never checked that a
// token could still be minted. Entra caps SPA refresh tokens at ~24h and Safari's
// tracking prevention blocks MSAL's hidden-iframe silent renewal, so an infrequent
// user's refresh token dies while the cached ACCOUNT OBJECT survives indefinitely.
// The app then showed their name in the header while every authenticated call failed
// with "no delegated token available" (Lydia and Ricky, intermittently, pre-1.2).
//
// So identity now has three states, not two:
//   authenticated  — cached account AND we believe a token is obtainable
//   expired        — cached account, but a silent token probe failed WHILE ONLINE
//   signed out     — no cached account at all
// `expired` is only ever set from an ONLINE probe: offline you cannot tell "refresh
// token is dead" from "no signal", and guessing wrong would nag a user standing on a
// show floor with no bars. Offline keeps showing the cached identity, unchanged.

import type {
  AccountInfo,
  Configuration,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AUTH, authConfigured } from '../config';

export interface Identity {
  /** Entra oid/localAccountId (real) or a synthetic id (stub/signed-out). */
  id: string;
  name: string;
  username: string;
  /** True only when backed by a real Entra account we believe can still mint a token. */
  authenticated: boolean;
  /**
   * Cached account present, but a silent token probe failed while online — the
   * refresh token is dead and only interactive sign-in can revive it. Never set
   * offline. Mutually exclusive with `authenticated`.
   */
  expired: boolean;
}

const STUB: Identity = {
  id: 'local-stub',
  name: 'Floor User (stub)',
  username: 'stub@local',
  authenticated: false,
  expired: false,
};

const SIGNED_OUT: Identity = {
  id: 'anonymous',
  name: 'Sign in',
  username: '',
  authenticated: false,
  expired: false,
};

let current: Identity = authConfigured() ? SIGNED_OUT : STUB;
let pca: PublicClientApplication | null = null;

// ── Change notification ─────────────────────────────────────────────────────
// The identity can now flip WITHOUT the user doing anything (a probe resolving,
// or a background sync discovering the token is dead), so the header can no
// longer just render once at mount. Listeners are called on every transition.
type AuthListener = (id: Identity) => void;
const listeners = new Set<AuthListener>();

/**
 * Subscribe to identity changes. Returns an unsubscribe.
 *
 * The unsubscribe returns nothing on purpose — app.ts registers it directly as a
 * `beforeunload` handler, and a handler that hands back a truthy value is the
 * legacy way of asking for a "Leave site?" prompt.
 */
export function onAuthChange(fn: AuthListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function setIdentity(next: Identity): Identity {
  const changed =
    current.id !== next.id ||
    current.authenticated !== next.authenticated ||
    current.expired !== next.expired ||
    current.name !== next.name;
  current = next;
  if (changed) {
    for (const fn of listeners) {
      try {
        fn(current);
      } catch (e) {
        console.warn('auth listener failed', e);
      }
    }
  }
  return current;
}

function msalConfig(): Configuration {
  return {
    auth: {
      clientId: AUTH.entraClientId,
      authority: `https://login.microsoftonline.com/${AUTH.tenantId}`,
      // Must be registered as a SPA redirect URI on the app reg. Default lands the
      // popup on the app's own page (origin + base path, e.g. .../booth-tracker/),
      // not the bare host root — covers localhost:5173/booth-tracker/ (dev) and the
      // SWA URL (prod) without per-env config. Overridable via VITE_REDIRECT_URI.
      redirectUri: AUTH.redirectUri || `${window.location.origin}${import.meta.env.BASE_URL}`,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      // localStorage so the account (and thus the cached identity) survives
      // reloads and offline launches — the offline-first requirement.
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  };
}

function identityFromAccount(acc: AccountInfo): Identity {
  return {
    id: acc.localAccountId || acc.homeAccountId,
    name: acc.name || acc.username,
    username: acc.username,
    authenticated: true,
    expired: false,
  };
}

/** The same account, re-flagged as needing interactive re-auth. */
function expiredFrom(id: Identity): Identity {
  return { ...id, authenticated: false, expired: true };
}

const isOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine;

/** The cached account, whichever way MSAL is holding it. */
function activeAccount(): AccountInfo | null {
  if (!pca) return null;
  return pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
}

/**
 * Initialise auth. Offline-safe: creates MSAL and restores a cached account from
 * localStorage (no network). Never throws in a way that blocks the UI — capture
 * must work even signed-out/offline.
 *
 * Deliberately does NOT probe the token: boot must not wait on a network round
 * trip. The caller (app.ts) kicks probeSession() once the header is mounted and
 * listening, so a dead session corrects itself a moment after paint rather than
 * delaying first paint for everyone.
 */
export async function initAuth(): Promise<Identity> {
  if (!authConfigured()) {
    return setIdentity(STUB);
  }
  try {
    const { PublicClientApplication } = await import('@azure/msal-browser');
    pca = new PublicClientApplication(msalConfig());
    await pca.initialize();
    const accounts = pca.getAllAccounts();
    if (accounts.length > 0) {
      pca.setActiveAccount(accounts[0]);
      setIdentity(identityFromAccount(accounts[0]));
    } else {
      setIdentity(SIGNED_OUT);
    }
  } catch (err) {
    // e.g. the MSAL chunk fails to load offline on a cold first run. Degrade to
    // signed-out; capture still works, sign-in can be retried when online.
    console.warn('auth init failed, continuing signed-out', err);
    setIdentity(SIGNED_OUT);
  }
  return current;
}

/**
 * Verify the cached session can still mint a token, WITHOUT any interaction.
 *
 * Call on startup (after mount) and whenever the app comes back online. Returns the
 * resulting identity. No-ops — and specifically does NOT clear `expired` — when
 * offline or when there is no cached account to probe.
 */
export async function probeSession(): Promise<Identity> {
  if (!authConfigured() || !pca) return current;
  const account = activeAccount();
  if (!account) return current;
  // OFFLINE IS NOT EXPIRED. Leave the cached identity exactly as it is.
  if (!isOnline()) return current;

  try {
    await pca.acquireTokenSilent({ scopes: [AUTH.mcpScope], account });
    // Silent renewal works — (re-)assert the healthy identity. This is also the
    // path that CLEARS a stale `expired` after the user signs back in elsewhere
    // or a flaky-network false positive resolves.
    return setIdentity(identityFromAccount(account));
  } catch (err) {
    console.warn('session probe failed; marking session expired', err);
    return setIdentity(expiredFrom(identityFromAccount(account)));
  }
}

/** Interactive sign-in (requires network). loginPopup per the brief. */
export async function signIn(): Promise<Identity> {
  if (!authConfigured() || !pca) return current;
  const result = await pca.loginPopup({ scopes: [AUTH.mcpScope] });
  if (result.account) {
    pca.setActiveAccount(result.account);
    setIdentity(identityFromAccount(result.account));
  }
  return current;
}

export async function signOut(): Promise<void> {
  if (!pca) return;
  const account = pca.getActiveAccount() ?? undefined;
  setIdentity(SIGNED_OUT);
  await pca.logoutPopup({ account });
}

export function getIdentity(): Identity {
  return current;
}

/**
 * The name to stamp on records this user creates.
 *
 * Kept separate from `authenticated` on purpose: an EXPIRED session still knows
 * exactly who the user is (the account object is intact — only its refresh token
 * died), so capture must keep attributing booths to "Brandon Penticuff" rather
 * than silently falling back to the login email. That fallback would quietly undo
 * the v0.5.2/v0.5.3 creator-attribution work for precisely the infrequent users
 * this release is about.
 */
export function attributionName(id: Identity = current): string {
  return id.authenticated || id.expired ? id.name : id.username;
}

/**
 * Delegated MCP/backend bearer token (audience api://<MCP_APP_ID>). Silent only,
 * from the cached refresh token. Returns null when signed-out, offline, or when
 * the silent call needs interaction — a sync must NEVER pop a window. Callers
 * (sync, snapshot) treat null as "try later"; the user re-auths via the sign-in
 * button, which is the only place interactive auth is allowed to surface.
 *
 * THIS IS THE BACKGROUND-SAFE ENTRY POINT AND MUST STAY THAT WAY. It may update the
 * identity to `expired` (so the header stops lying the moment a sync discovers the
 * token is dead) but it never, under any circumstance, opens a window.
 */
export async function getMcpToken(): Promise<string | null> {
  if (!authConfigured() || !pca) return null;
  const account = activeAccount();
  if (!account) return null;
  try {
    const res = await pca.acquireTokenSilent({ scopes: [AUTH.mcpScope], account });
    // A silent success also clears a stale `expired` — e.g. the user re-authed in
    // another tab, or an earlier probe tripped on a flaky connection.
    if (current.expired) setIdentity(identityFromAccount(account));
    return res.accessToken;
  } catch (err) {
    // interaction_required / expired refresh token / offline — do not pop a
    // window mid-sync. Return null; the sign-in button drives re-auth.
    console.warn('silent token acquisition failed; re-auth via sign-in', err);
    // Only an ONLINE failure is evidence of expiry. Offline this is just "no
    // signal", and flipping the header to "Session expired" on the show floor
    // would be a lie plus a nag. A false positive from a flaky-but-online
    // connection is self-correcting: the next probe or silent success clears it.
    if (isOnline() && !current.expired) {
      setIdentity(expiredFrom(identityFromAccount(account)));
    }
    return null;
  }
}

/**
 * Token for a USER-INITIATED action, allowed to go interactive.
 *
 * Silent first; if that can't produce a token and we're online, sign in right here
 * and hand back the fresh token so the caller's action just proceeds — one tap, no
 * red error, no "sign out and back in" dance.
 *
 * ⚠️ iOS Safari blocks a popup that isn't opened inside the user's tap. When the
 * session is ALREADY KNOWN to be expired we therefore skip the silent attempt and
 * call loginPopup() straight away: every `await` between the tap handler and this
 * function calls the next function synchronously before suspending, so the popup
 * still opens in the gesture's own task. Do not "tidy" that fast path away by
 * always probing first — the await would land after the gesture is spent, and
 * Safari would eat the popup.
 *
 * Only ever reachable from user-initiated paths (vendor 360 product list, product
 * 360, snapshot build). Background sync uses getMcpToken() and must keep doing so.
 */
export async function getMcpTokenInteractive(): Promise<string | null> {
  if (!authConfigured() || !pca) return null;
  // Nothing interactive is possible offline, and we must not claim expiry there.
  if (!isOnline()) return getMcpToken();

  // Known-dead session → popup NOW, while we still hold the user's gesture.
  if (current.expired || !activeAccount()) {
    return signInAndGetToken();
  }

  const silent = await getMcpToken();
  if (silent) return silent;

  // The token died since the last probe. Worth one interactive attempt: it works
  // on desktop and usually on Android. If Safari swallows the popup the caller
  // gets null, surfaces its own "Session expired — sign in" affordance, and the
  // retry from THAT tap does hold a live gesture.
  if (!isOnline()) return null;
  return signInAndGetToken();
}

/** loginPopup, then the token it just made possible. Null if the user cancels. */
async function signInAndGetToken(): Promise<string | null> {
  if (!pca) return null;
  try {
    const result = await pca.loginPopup({ scopes: [AUTH.mcpScope] });
    if (!result.account) return null;
    pca.setActiveAccount(result.account);
    setIdentity(identityFromAccount(result.account));
    // loginPopup's own accessToken is scoped to this request, so prefer it; fall
    // back to a silent call (now backed by a fresh refresh token) if absent.
    if (result.accessToken) return result.accessToken;
    const res = await pca.acquireTokenSilent({
      scopes: [AUTH.mcpScope],
      account: result.account,
    });
    return res.accessToken;
  } catch (err) {
    // Cancelled, popup blocked, or the network died mid-flow. Not fatal — the
    // caller degrades to its own sign-in affordance.
    console.warn('interactive token acquisition failed', err);
    return null;
  }
}

export const isStubbed = (): boolean => !authConfigured();
